import React, { useEffect, useRef, useState } from 'react';
import { Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';

// Custom Google "G" logo. Purely static (no props/closures), so it's
// hoisted out of GoogleSignInButton rather than declared - and thus
// recreated as a brand-new function identity - on every one of its
// renders. It's rendered as JSX (<GoogleGLogo />, not called directly),
// so a fresh identity each render means React treats it as a different
// component type at that position and remounts it instead of reusing the
// existing DOM. GoogleSignInButton has no memo and lives inside Login.js/
// Register.js/LandingPage.js, all of which re-render on every keystroke in
// their own form fields - so this SVG was being torn down and rebuilt on
// every character typed into those forms.
const GoogleGLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// invitationCode: optional. When provided (sign-up page), it's sent with the
// first Google login attempt instead of prompting for it afterwards.
const GoogleSignInButton = ({ onSuccess, onError, disabled = false, invitationCode: providedInvitationCode }) => {
  const googleButtonRef = useRef(null);
  // google.accounts.id.initialize() captures handleGoogleResponse once, so
  // read the latest code through a ref rather than the render-time prop.
  const providedInvitationCodeRef = useRef(providedInvitationCode);
  providedInvitationCodeRef.current = providedInvitationCode;
  const scriptLoadedRef = useRef(false);
  const googleInitialized = useRef(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [invitationCode, setInvitationCode] = useState('');
  const [inviteError, setInviteError] = useState('');
  const pendingCredentialRef = useRef(null);

  useEffect(() => {
    // Load Google Identity Services script if not already loaded
    if (!scriptLoadedRef.current) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Google Identity Services script loaded');
        scriptLoadedRef.current = true;
        initializeGoogleSignIn();
      };
      script.onerror = () => {
        console.error('Failed to load Google Identity Services script');
        onError('Failed to load Google Sign-In');
      };
      document.head.appendChild(script);
    } else if (window.google && !googleInitialized.current) {
      initializeGoogleSignIn();
    }

    return () => {
      // Cleanup on unmount
      const script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (script && !scriptLoadedRef.current) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const initializeGoogleSignIn = () => {
    if (!window.google) {
      console.error('Google not available on window');
      return;
    }

    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      console.error('Google Client ID not configured');
      // Don't call onError during initialization to avoid interfering with other error messages
      // Only show error when user actually tries to use Google Sign-In
      return;
    }

    if (googleInitialized.current) {
      console.log('Google already initialized');
      return;
    }

    try {
      console.log('Initializing Google Sign-In with Client ID:', process.env.REACT_APP_GOOGLE_CLIENT_ID.substring(0, 20) + '...');
      
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: true
      });
      
      googleInitialized.current = true;
      console.log('Google Sign-In initialized successfully');
    } catch (error) {
      console.error('Error initializing Google Sign-In:', error);
      onError('Failed to initialize Google Sign-In');
    }
  };

  const handleGoogleResponse = (response) => {
    console.log('Google response received:', response);
    if (response.credential) {
      console.log('Google credential received, attempting login first');
      // Try login without invitation code first (for existing users)
      attemptGoogleLogin(response.credential);
    } else {
      console.error('No credential in Google response');
      onError('Failed to get Google credentials');
    }
  };

  const attemptGoogleLogin = async (credential) => {
    const presetCode = providedInvitationCodeRef.current?.trim() || null;
    try {
      // Without a preset code, try first without one (for existing users)
      await onSuccess(credential, presetCode);
    } catch (error) {
      if (presetCode && error.response?.data?.requires_invitation_code) {
        // The code entered on the page was rejected - report it there
        onError(error.response?.data?.error || 'Invalid invitation code');
      } else if (error.response?.data?.requires_invitation_code) {
        // If backend indicates invitation code is needed, show dialog
        console.log('New user detected, showing invitation code dialog');
        pendingCredentialRef.current = credential;
        setShowInviteDialog(true);
      } else {
        // Some other error occurred
        console.error('Google login error:', error);
        onError(error.response?.data?.error || 'Google sign-in failed');
      }
    }
  };

  const handleInviteSubmit = () => {
    if (!invitationCode.trim()) {
      setInviteError('Invitation code is required');
      return;
    }

    // Call onSuccess with both credential and invitation code
    onSuccess(pendingCredentialRef.current, invitationCode.trim());

    // Reset state
    setShowInviteDialog(false);
    setInvitationCode('');
    setInviteError('');
    pendingCredentialRef.current = null;
  };

  const handleInviteCancel = () => {
    setShowInviteDialog(false);
    setInvitationCode('');
    setInviteError('');
    pendingCredentialRef.current = null;
  };

  const handleSignIn = () => {
    console.log('Google Sign-In button clicked');
    
    if (!window.google) {
      console.error('Google not loaded');
      onError('Google Sign-In not loaded');
      return;
    }

    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      console.error('Google Client ID not configured');
      onError('Google Sign-In not configured in development environment');
      return;
    }

    if (!googleInitialized.current) {
      console.log('Google not initialized, trying to initialize now');
      initializeGoogleSignIn();
      
      // Wait a bit and try again
      setTimeout(() => {
        if (googleInitialized.current) {
          triggerGoogleSignIn();
        } else {
          onError('Google Sign-In initialization failed');
        }
      }, 500);
    } else {
      triggerGoogleSignIn();
    }
  };

  const triggerGoogleSignIn = () => {
    try {
      console.log('Triggering Google Sign-In prompt');
      
      // Directly trigger the Google sign-in flow
      window.google.accounts.id.prompt((notification) => {
        console.log('Google prompt notification:', notification);

        const momentType = typeof notification.getMomentType === 'function'
          ? notification.getMomentType()
          : notification?.momentType;

        const dismissedReason = typeof notification.getDismissedReason === 'function'
          ? notification.getDismissedReason()
          : notification?.dismissedReason ?? notification?.dismissed_reason;

        console.log('FedCM prompt moment:', momentType, 'dismissed reason:', dismissedReason);

        const normalizedMomentType = typeof momentType === 'string' ? momentType.toLowerCase() : momentType;
        const normalizedDismissedReason = typeof dismissedReason === 'string' ? dismissedReason.toLowerCase() : dismissedReason;

        const fallbackDismissedReasons = new Set([
          'auto_cancel',
          'cancel_called',
          'ineligible',
          'issuer_not_found',
          'missing_client_id',
          'opt_out',
          'suppressed_by_user',
          'tap_outside',
          'tapped_outside',
          'third_party_cookies_blocked',
          'unknown',
          'unknown_reason',
          'user_cancel'
        ]);

        const shouldFallback = (() => {
          const legacyNotDisplayed = typeof notification.isNotDisplayed === 'function' && notification.isNotDisplayed();
          const legacySkipped = typeof notification.isSkippedMoment === 'function' && notification.isSkippedMoment();

          if (legacyNotDisplayed || legacySkipped) {
            return true;
          }

          if (!normalizedMomentType) {
            return false;
          }

          if (normalizedMomentType === 'skipped') {
            return true;
          }

          if (normalizedMomentType === 'dismissed') {
            if (!normalizedDismissedReason) {
              return true;
            }

            if (normalizedDismissedReason === 'credential_returned') {
              return false;
            }

            return fallbackDismissedReasons.has(normalizedDismissedReason) || typeof normalizedDismissedReason === 'string';
          }

          if (normalizedMomentType === 'dismissed_reason') {
            return true;
          }

          if (normalizedMomentType === 'display_cancelled' || normalizedMomentType === 'not_displayed') {
            return true;
          }

          return false;
        })();

        if (shouldFallback) {
          console.log('One Tap not available, trying direct sign-in');
          
          // If One Tap is not available, try to directly trigger the sign-in
          // by creating a temporary button and clicking it immediately
          try {
            // Create a temporary container
            const tempContainer = document.createElement('div');
            tempContainer.style.cssText = `
              position: absolute;
              left: -9999px;
              top: -9999px;
              width: 1px;
              height: 1px;
              overflow: hidden;
            `;
            document.body.appendChild(tempContainer);
            
            // Render the Google Sign-In button in the hidden container
            window.google.accounts.id.renderButton(
              tempContainer,
              {
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                shape: 'rectangular',
                width: 250
              }
            );
            
            // Find and click the Google button immediately
            setTimeout(() => {
              const googleButton = tempContainer.querySelector('div[role="button"]') ||
                                   tempContainer.querySelector('button') ||
                                   tempContainer.querySelector('[data-testid="google-signin-button"]');
              
              if (googleButton) {
                console.log('Found Google button, clicking it');
                googleButton.click();
              } else {
                console.log('Google button not found, trying alternative approach');
                // If we can't find the button, try to trigger the sign-in directly
                window.google.accounts.id.prompt();
              }
              
              // Clean up the temporary container
              setTimeout(() => {
                if (document.body.contains(tempContainer)) {
                  document.body.removeChild(tempContainer);
                }
              }, 1000);
            }, 50);
            
          } catch (fallbackError) {
            console.error('Fallback sign-in error:', fallbackError);
            // Last resort: try to prompt again
            window.google.accounts.id.prompt();
          }
        }
      });
      
    } catch (error) {
      console.error('Error triggering Google Sign-In:', error);
      onError('Failed to show Google Sign-In');
    }
  };

  return (
    <>
      <Button
        ref={googleButtonRef}
        fullWidth
        variant="contained"
        startIcon={<GoogleGLogo />}
        onClick={handleSignIn}
        disabled={disabled}
        sx={{
        bgcolor: 'white',
        color: '#4285f4',
        border: 'none',
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 'bold',
        py: 1.5,
        px: 3,
        borderRadius: 2,
        boxShadow: '0 6px 20px rgba(66, 133, 244, 0.3), 0 3px 8px rgba(0, 0, 0, 0.2), inset 0 2px 0 rgba(255, 255, 255, 0.9), inset 0 -1px 0 rgba(0, 0, 0, 0.1)',
        '&:hover': {
          bgcolor: '#f8f9fa',
          color: '#357ae8',
          boxShadow: '0 12px 32px rgba(66, 133, 244, 0.4), 0 6px 16px rgba(0, 0, 0, 0.25), inset 0 2px 0 rgba(255, 255, 255, 1), inset 0 -1px 0 rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-4px) scale(1.02)'
        },
        '&:active': {
          transform: 'translateY(-1px) scale(0.98)',
          boxShadow: '0 2px 8px rgba(66, 133, 244, 0.2), 0 1px 4px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7), inset 0 -2px 0 rgba(0, 0, 0, 0.2)'
        },
        '&:disabled': {
          bgcolor: '#ccc',
          color: '#666',
          boxShadow: 'none',
          transform: 'none'
        },
        transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '2px',
          left: '2px',
          right: '2px',
          bottom: '2px',
          borderRadius: 1,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 50%, rgba(0,0,0,0.05) 100%)',
          opacity: 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
          zIndex: 1
        },
        '&:hover::before': {
          opacity: 1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(66, 133, 244, 0.1) 0%, rgba(66, 133, 244, 0.05) 100%)',
          opacity: 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none'
        },
        '&:hover::after': {
          opacity: 1
        }
      }}
      >
        Continue with Google
      </Button>

      {/* Invitation Code Dialog */}
      <Dialog open={showInviteDialog} onClose={handleInviteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Enter Invitation Code</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Invitation Code"
            type="text"
            fullWidth
            variant="outlined"
            value={invitationCode}
            onChange={(e) => {
              setInvitationCode(e.target.value);
              setInviteError('');
            }}
            error={!!inviteError}
            helperText={inviteError || "Enter the invitation code you received"}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleInviteSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInviteCancel}>Cancel</Button>
          <Button onClick={handleInviteSubmit} variant="contained" color="primary">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GoogleSignInButton; 